import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  User, Building2, Mail, Phone, MapPin, Calendar,
  Beaker, Package, FileText, Upload, Loader2, CheckCircle2,
  AlertCircle, ChevronRight, Sparkles, Save, Send, X,
  Clock, Star, ArrowRight, Shield
} from "lucide-react";

// Indian States
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

// Sample Priority Options
const SAMPLE_PRIORITIES = ["High", "Medium", "Low"];

// Product Type Hierarchy (Matching Zoho Creator Field Names)
const PRODUCT_HIERARCHY = {
  "Skin Care Products": {
    "Cleansers": ["Face Wash", "Cleansing Milk", "Micellar Water", "Makeup Remover", "Other"],
    "Exfoliators": ["Scrub", "Peel", "Enzyme Exfoliator", "AHA/BHA", "Other"],
    "Serums": ["Vitamin C", "Hyaluronic Acid", "Retinol", "Niacinamide", "Peptide", "Other"],
    "Toners & Mists": ["Hydrating Toner", "Astringent", "Face Mist", "Essence", "Other"],
    "Oils & Balms": ["Face Oil", "Cleansing Balm", "Lip Balm", "Multi-purpose Balm", "Other"],
    "Moisturizers": ["Day Cream", "Night Cream", "Gel Moisturizer", "Lotion", "Other"],
    "Masks": ["Sheet Mask", "Clay Mask", "Peel-off Mask", "Overnight Mask", "Other"],
    "Sunscreens": ["Chemical SPF", "Physical SPF", "Hybrid SPF", "Tinted SPF", "Other"],
    "Spot Treatments": ["Acne Treatment", "Dark Spot Corrector", "Blemish Patches", "Other"],
    "Eye Care": ["Eye Cream", "Eye Serum", "Under Eye Mask", "Other"],
    "Lip Care": ["Lip Balm", "Lip Scrub", "Lip Mask", "Lip Serum", "Other"],
    "Anti-Ageing / Special Care": ["Anti-Wrinkle", "Firming", "Lifting", "Brightening", "Other"],
    "Specialty / Functional": ["Pore Minimizer", "Redness Relief", "Barrier Repair", "Other"],
    "Body Care (Skin Focused)": ["Body Lotion", "Body Butter", "Body Oil", "Hand Cream", "Other"]
  },
  "Hair Care Products": {
    "Shampoos": ["Daily Use", "Anti-Dandruff", "Color Protection", "Volumizing", "Clarifying", "Other"],
    "Conditioners": ["Daily Conditioner", "Deep Conditioner", "Leave-in", "Color Safe", "Other"],
    "Hair Masks / Treatments": ["Hair Mask", "Hair Spa", "Protein Treatment", "Keratin", "Other"],
    "Hair Oils": ["Growth Oil", "Argan Oil", "Coconut Blend", "Hot Oil Treatment", "Other"],
    "Serums & Leave-ins": ["Hair Serum", "Leave-in Conditioner", "Heat Protectant", "Other"],
    "Hair Styling Products": ["Hair Gel", "Hair Wax", "Hair Spray", "Mousse", "Other"],
    "Hair Growth & Anti-Hair Fall": ["Growth Serum", "Anti-Fall Shampoo", "Scalp Tonic", "Other"],
    "Hair Color & Dye": ["Permanent Color", "Semi-Permanent", "Highlights", "Root Touch-up", "Other"],
    "Scalp Care": ["Scalp Scrub", "Scalp Serum", "Anti-Dandruff Treatment", "Other"]
  },
  "Oral Care Products": {
    "Toothpaste": ["Whitening", "Sensitivity", "Herbal", "Fluoride", "Kids", "Other"],
    "Mouthwash": ["Antiseptic", "Whitening", "Fresh Breath", "Alcohol-Free", "Other"]
  }
};

// Texture Options (matching Zoho Texture field)
const TEXTURE_OPTIONS = [
  "Gel", "Cream", "Lotion", "Serum", "Oil", "Foam", "Powder", 
  "Balm", "Spray", "Mist", "Paste", "Liquid", "Solid", "Mousse",
  "Butter", "Wax", "Clay", "Emulsion", "Other"
];

// Color Options (matching Zoho Color field)
const COLOR_OPTIONS = [
  "Transparent", "White", "Off-White", "Cream", "Yellow", "Orange",
  "Pink", "Red", "Green", "Blue", "Purple", "Brown", "Black", "Gold", 
  "Silver", "Peach", "Lavender", "Beige", "Ivory", "Clear", "Other"
];

// Odour/Fragrance Options (matching Zoho Odour_Fragrance field)
const ODOUR_FRAGRANCE_OPTIONS = [
  "Floral", "Citrus", "Herbal", "Fruity", "Woody", "Spicy", "Fresh",
  "Minty", "Sweet", "Earthy", "Musky", "Vanilla", "Coconut", "Rose",
  "Lavender", "Jasmine", "Sandalwood", "Fragrance-Free", "Unscented", "Other"
];

// Product Purpose Options (matching Zoho Product_Purpose field)
const PRODUCT_PURPOSE_OPTIONS = [
  "Daily Use", "Treatment", "Occasional Use", "Professional Use",
  "Therapeutic", "Cosmetic", "Functional", "Luxury", "Hydration",
  "Anti-Aging", "Brightening", "Acne Control", "Sun Protection",
  "Hair Growth", "Dandruff Control", "Color Protection", "Other"
];

// Skin/Hair Type Options (matching Zoho Skin_Hair_Type field)
const SKIN_HAIR_TYPE_OPTIONS = [
  "Normal", "Dry", "Oily", "Combination", "Sensitive", "All Types",
  "Damaged", "Color Treated", "Fine", "Thick", "Curly", "Straight",
  "Wavy", "Coily", "Mature", "Acne-Prone", "Dehydrated", "Other"
];

// Pack Size Options (matching Zoho Requirement_Pack_Size field - up to 30KG)
const PACK_SIZE_OPTIONS = [
  "5ml", "10ml", "15ml", "20ml", "25ml", "30ml", "50ml", "75ml", "100ml",
  "125ml", "150ml", "200ml", "250ml", "300ml", "400ml", "500ml", "750ml", "1L",
  "5g", "10g", "15g", "20g", "25g", "30g", "50g", "75g", "100g",
  "150g", "200g", "250g", "300g", "400g", "500g", "750g", "1KG",
  "2KG", "5KG", "10KG", "15KG", "20KG", "25KG", "30KG", "Other"
];

// Packaging Options (matching Zoho Packaging_Preference field)
const PACKAGING_OPTIONS = [
  "Tube", "Bottle", "Jar", "Pump Bottle", "Spray Bottle", "Dropper Bottle",
  "Sachet", "Pouch", "Stick", "Compact", "Airless Pump", "Roll-On",
  "Flip-Top Cap", "Disc Cap", "Squeeze Tube", "Tin", "Box", "Carton",
  "Blister Pack", "Ampoule", "Vial", "Other"
];

// Ingredients Options (matching Zoho Ingredients field)
const INGREDIENTS_OPTIONS = [
  "Natural/Organic", "Paraben-Free", "Sulfate-Free", "Silicone-Free",
  "Fragrance-Free", "Alcohol-Free", "Vegan", "Cruelty-Free",
  "Hypoallergenic", "Dermatologist Tested", "pH Balanced",
  "Ayurvedic", "Herbal", "Chemical-Free", "No Preference", "Other"
];

// Mandatory fields for validation
const MANDATORY_FIELDS = [
  { key: "firstName", label: "Client Name" },
  { key: "email", label: "Email Address" },
  { key: "companyName", label: "Company Name" },
  { key: "phone", label: "Phone Number" },
  { key: "location", label: "Location" },
  { key: "samplePriority", label: "Sample Priority" },
  { key: "productType", label: "Product Type" },
  { key: "category", label: "Category" }
];

export default function ClientRequirementFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [activeSection, setActiveSection] = useState(1);
  
  // Form State
  const [formData, setFormData] = useState({
    // Client Details
    firstName: "",
    email: "",
    companyName: "",
    phone: "",
    countryCode: "+91",
    location: "",
    samplePriority: "",
    
    // Product Requirement
    texture: "",
    textureOther: "",  // Custom "Other" value for texture
    productPurpose: "",
    productPurposeOther: "",  // Custom "Other" value for product purpose
    color: "",
    colorOther: "",  // Custom "Other" value for color
    odourFragrance: "",
    odourFragranceOther: "",  // Custom "Other" value for odour/fragrance
    skinHairType: "",
    skinHairTypeOther: "",  // Custom "Other" value for skin/hair type
    requiredQuantity: "",
    ingredientsPreference: "",
    ingredientsPreferenceOther: "",  // Custom "Other" value for ingredients
    requirementPackSize: "",
    requirementPackSizeOther: "",  // Custom "Other" value for pack size
    packagingPreference: "",
    packagingPreferenceOther: "",  // Custom "Other" value for packaging
    
    // Product Details (3-level hierarchy)
    productType: "",
    category: "",
    subCategory: "",
    productDescription: "",
    
    // Remarks
    referenceImage: null,
    referenceImageUrl: "",
    expectedDeliveryDate: "",
    remarks: ""
  });

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('clientRequirementDraft');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        setFormData(prev => ({ ...prev, ...parsed }));
        toast.info("Draft loaded from previous session", { duration: 3000 });
      } catch (e) {
        console.error("Error loading draft:", e);
      }
    }
  }, []);

  // Get categories based on selected product type
  const getCategories = () => {
    if (!formData.productType) return [];
    return Object.keys(PRODUCT_HIERARCHY[formData.productType] || {});
  };

  // Get sub-categories based on selected category
  const getSubCategories = () => {
    if (!formData.productType || !formData.category) return [];
    return PRODUCT_HIERARCHY[formData.productType]?.[formData.category] || [];
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset dependent fields when parent changes
      if (field === "productType") {
        updated.category = "";
        updated.subCategory = "";
      }
      if (field === "category") {
        updated.subCategory = "";
      }
      
      return updated;
    });
    
    // Clear validation error for this field
    setValidationErrors(prev => prev.filter(err => err.key !== field));
  };

  // Validate form
  const validateForm = () => {
    const errors = [];
    MANDATORY_FIELDS.forEach(field => {
      if (!formData[field.key] || formData[field.key].trim() === "") {
        errors.push(field);
      }
    });
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }
    
    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          referenceImage: file,
          referenceImageUrl: reader.result
        }));
        setUploadingImage(false);
        toast.success("Image uploaded successfully");
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadingImage(false);
      toast.error("Failed to upload image");
    }
  };

  // Save as Draft
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const draftData = { ...formData };
      delete draftData.referenceImage; // Don't save file object
      localStorage.setItem('clientRequirementDraft', JSON.stringify(draftData));
      toast.success("Draft saved successfully! You can continue later.", {
        duration: 4000,
        icon: <Save className="w-5 h-5 text-green-500" />
      });
    } catch (error) {
      toast.error("Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  // Clear draft
  const clearDraft = () => {
    localStorage.removeItem('clientRequirementDraft');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      toast.error("Please fill in all required fields", {
        description: `${validationErrors.length} field(s) need attention`,
        duration: 5000
      });
      return;
    }
    
    setSubmitting(true);
    try {
      // Helper to merge "Other" custom values into main field
      const mergeOtherValue = (mainValue, otherValue) => {
        if (mainValue === "Other" && otherValue && otherValue.trim()) {
          return otherValue.trim();
        }
        return mainValue;
      };
      
      const payload = {
        name: formData.firstName,
        firstName: formData.firstName,
        lastName: "",
        email: formData.email,
        companyName: formData.companyName,
        phoneNumber: `${formData.countryCode}${formData.phone}`,
        location: formData.location,
        samplePriority: formData.samplePriority,
        // Merge "Other" values into main fields
        texture: mergeOtherValue(formData.texture, formData.textureOther),
        productPurpose: mergeOtherValue(formData.productPurpose, formData.productPurposeOther),
        color: mergeOtherValue(formData.color, formData.colorOther),
        odourFragrance: mergeOtherValue(formData.odourFragrance, formData.odourFragranceOther),
        skinHairType: mergeOtherValue(formData.skinHairType, formData.skinHairTypeOther),
        requiredQuantity: formData.requiredQuantity,
        ingredientsPreference: mergeOtherValue(formData.ingredientsPreference, formData.ingredientsPreferenceOther),
        requirementPackSize: mergeOtherValue(formData.requirementPackSize, formData.requirementPackSizeOther),
        packagingPreference: mergeOtherValue(formData.packagingPreference, formData.packagingPreferenceOther),
        productType: formData.productType,
        category: formData.category,
        subCategory: formData.subCategory,
        productDescription: formData.productDescription,
        referenceImageBase64: formData.referenceImageUrl,
        expectedDeliveryDate: formData.expectedDeliveryDate,
        remarks: formData.remarks
      };

      const response = await axios.post(`${API}/client-requirements`, payload);
      
      if (response.data.success) {
        clearDraft();
        setSubmissionResult({
          sampleNo: response.data.zohoRecordId || response.data.localId || "Pending",
          clientName: formData.firstName,
          companyName: formData.companyName,
          productType: formData.productType,
          category: formData.category,
          subCategory: formData.subCategory,
          priority: formData.samplePriority,
          syncedToZoho: response.data.syncedToZoho
        });
        setShowSuccessDialog(true);
        setSubmitted(true);
      } else {
        toast.error(response.data.error || "Failed to submit requirement");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      firstName: "",
      email: "",
      companyName: "",
      phone: "",
      countryCode: "+91",
      location: "",
      samplePriority: "",
      texture: "",
      textureOther: "",
      productPurpose: "",
      productPurposeOther: "",
      color: "",
      colorOther: "",
      odourFragrance: "",
      odourFragranceOther: "",
      skinHairType: "",
      skinHairTypeOther: "",
      requiredQuantity: "",
      ingredientsPreference: "",
      ingredientsPreferenceOther: "",
      requirementPackSize: "",
      requirementPackSizeOther: "",
      packagingPreference: "",
      packagingPreferenceOther: "",
      productType: "",
      category: "",
      subCategory: "",
      productDescription: "",
      referenceImage: null,
      referenceImageUrl: "",
      expectedDeliveryDate: "",
      remarks: ""
    });
    setSubmitted(false);
    setShowSuccessDialog(false);
    setValidationErrors([]);
    clearDraft();
  };

  // Field error check
  const hasError = (fieldKey) => validationErrors.some(err => err.key === fieldKey);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      {/* Decorative Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-200/20 to-cyan-200/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-rose-200/20 to-pink-200/20 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with Logo */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-center mb-4">
            <img 
              src="/vcc-logo.png" 
              alt="V Cosmetic Crafters" 
              className="h-16 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 font-poppins">
            Client Requirement Form
          </h1>
          <p className="text-slate-500 mt-2 font-poppins">
            V Cosmetic Crafters Private Limited
          </p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Shield className="w-4 h-4 text-teal-600" />
            <span className="text-sm text-slate-500 font-poppins">Your data is secure and confidential</span>
          </div>
        </motion.div>

        {/* Progress Steps */}
        <motion.div 
          className="flex justify-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-4 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-slate-100">
            {[
              { num: 1, label: "Client Info" },
              { num: 2, label: "Product Details" },
              { num: 3, label: "Requirements" },
              { num: 4, label: "Submit" }
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <button
                  onClick={() => setActiveSection(step.num)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                    activeSection === step.num 
                      ? 'bg-teal-600 text-white shadow-md' 
                      : activeSection > step.num
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 text-sm font-semibold">
                    {activeSection > step.num ? <CheckCircle2 className="w-4 h-4" /> : step.num}
                  </span>
                  <span className="hidden md:inline text-sm font-medium font-poppins">{step.label}</span>
                </button>
                {idx < 3 && <ChevronRight className="w-4 h-4 text-slate-300 mx-1" />}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Validation Errors Alert */}
        <AnimatePresence>
          {validationErrors.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-800 font-poppins">Please complete required fields</h4>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {validationErrors.map(err => (
                      <Badge key={err.key} className="bg-red-100 text-red-700 font-poppins">
                        {err.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Section 1: Client Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className={`overflow-hidden transition-all ${activeSection === 1 ? 'ring-2 ring-teal-500 shadow-xl' : 'shadow-lg'}`}>
                <div 
                  className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 cursor-pointer"
                  onClick={() => setActiveSection(1)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white font-poppins">Client Information</h2>
                      <p className="text-teal-100 text-sm font-poppins">Your contact and company details</p>
                    </div>
                  </div>
                </div>
                
                <AnimatePresence>
                  {activeSection === 1 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Client Name */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('firstName') ? 'text-red-600' : 'text-slate-700'}`}>
                              Client Name <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                value={formData.firstName}
                                onChange={(e) => handleChange("firstName", e.target.value)}
                                placeholder="Enter your full name"
                                className={`pl-10 h-11 font-poppins ${hasError('firstName') ? 'border-red-500 focus:ring-red-500' : 'border-slate-200'}`}
                                data-testid="input-first-name"
                              />
                            </div>
                          </div>

                          {/* Company Name */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('companyName') ? 'text-red-600' : 'text-slate-700'}`}>
                              Company Name <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                value={formData.companyName}
                                onChange={(e) => handleChange("companyName", e.target.value)}
                                placeholder="Your company name"
                                className={`pl-10 h-11 font-poppins ${hasError('companyName') ? 'border-red-500 focus:ring-red-500' : 'border-slate-200'}`}
                                data-testid="input-company-name"
                              />
                            </div>
                          </div>

                          {/* Email */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('email') ? 'text-red-600' : 'text-slate-700'}`}>
                              Email Address <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleChange("email", e.target.value)}
                                placeholder="your@email.com"
                                className={`pl-10 h-11 font-poppins ${hasError('email') ? 'border-red-500 focus:ring-red-500' : 'border-slate-200'}`}
                                data-testid="input-email"
                              />
                            </div>
                          </div>

                          {/* Phone */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('phone') ? 'text-red-600' : 'text-slate-700'}`}>
                              Phone Number <span className="text-red-500">*</span>
                            </Label>
                            <div className="flex gap-2">
                              <Select value={formData.countryCode} onValueChange={(v) => handleChange("countryCode", v)}>
                                <SelectTrigger className="w-24 h-11 font-poppins">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="+91">+91</SelectItem>
                                  <SelectItem value="+1">+1</SelectItem>
                                  <SelectItem value="+44">+44</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="relative flex-1">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                  value={formData.phone}
                                  onChange={(e) => handleChange("phone", e.target.value)}
                                  placeholder="9876543210"
                                  className={`pl-10 h-11 font-poppins ${hasError('phone') ? 'border-red-500 focus:ring-red-500' : 'border-slate-200'}`}
                                  data-testid="input-phone"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Location */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('location') ? 'text-red-600' : 'text-slate-700'}`}>
                              Location <span className="text-red-500">*</span>
                            </Label>
                            <Select value={formData.location} onValueChange={(v) => handleChange("location", v)}>
                              <SelectTrigger className={`h-11 font-poppins ${hasError('location') ? 'border-red-500' : ''}`} data-testid="select-location">
                                <MapPin className="w-4 h-4 text-slate-400 mr-2" />
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {INDIAN_STATES.map(state => (
                                  <SelectItem key={state} value={state} className="font-poppins">{state}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Sample Priority */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('samplePriority') ? 'text-red-600' : 'text-slate-700'}`}>
                              Sample Priority <span className="text-red-500">*</span>
                            </Label>
                            <Select value={formData.samplePriority} onValueChange={(v) => handleChange("samplePriority", v)}>
                              <SelectTrigger className={`h-11 font-poppins ${hasError('samplePriority') ? 'border-red-500' : ''}`} data-testid="select-priority">
                                <Star className="w-4 h-4 text-slate-400 mr-2" />
                                <SelectValue placeholder="Select priority" />
                              </SelectTrigger>
                              <SelectContent>
                                {SAMPLE_PRIORITIES.map(priority => (
                                  <SelectItem key={priority} value={priority} className="font-poppins">
                                    <span className={`flex items-center gap-2 ${
                                      priority === 'High' ? 'text-red-600' : 
                                      priority === 'Medium' ? 'text-amber-600' : 'text-green-600'
                                    }`}>
                                      {priority}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex justify-end mt-6">
                          <Button 
                            type="button" 
                            onClick={() => setActiveSection(2)}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-poppins"
                          >
                            Next: Product Details <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>

            {/* Section 2: Product Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className={`overflow-hidden transition-all ${activeSection === 2 ? 'ring-2 ring-blue-500 shadow-xl' : 'shadow-lg'}`}>
                <div 
                  className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 cursor-pointer"
                  onClick={() => setActiveSection(2)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white font-poppins">Product Details</h2>
                      <p className="text-blue-100 text-sm font-poppins">Select product type and category</p>
                    </div>
                  </div>
                </div>
                
                <AnimatePresence>
                  {activeSection === 2 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Product Type */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('productType') ? 'text-red-600' : 'text-slate-700'}`}>
                              Product Type <span className="text-red-500">*</span>
                            </Label>
                            <Select value={formData.productType} onValueChange={(v) => handleChange("productType", v)}>
                              <SelectTrigger className={`h-11 font-poppins ${hasError('productType') ? 'border-red-500' : ''}`} data-testid="select-product-type">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.keys(PRODUCT_HIERARCHY).map(type => (
                                  <SelectItem key={type} value={type} className="font-poppins">{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Category */}
                          <div className="space-y-2">
                            <Label className={`font-poppins ${hasError('category') ? 'text-red-600' : 'text-slate-700'}`}>
                              Category <span className="text-red-500">*</span>
                            </Label>
                            <Select 
                              value={formData.category} 
                              onValueChange={(v) => handleChange("category", v)}
                              disabled={!formData.productType}
                            >
                              <SelectTrigger className={`h-11 font-poppins ${hasError('category') ? 'border-red-500' : ''}`} data-testid="select-category">
                                <SelectValue placeholder={formData.productType ? "Select category" : "Select product type first"} />
                              </SelectTrigger>
                              <SelectContent>
                                {getCategories().map(cat => (
                                  <SelectItem key={cat} value={cat} className="font-poppins">{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Sub-Category */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Sub-Category</Label>
                            <Select 
                              value={formData.subCategory} 
                              onValueChange={(v) => handleChange("subCategory", v)}
                              disabled={!formData.category}
                            >
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-sub-category">
                                <SelectValue placeholder={formData.category ? "Select sub-category" : "Select category first"} />
                              </SelectTrigger>
                              <SelectContent>
                                {getSubCategories().map(sub => (
                                  <SelectItem key={sub} value={sub} className="font-poppins">{sub}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Selected Path Display */}
                        {formData.productType && (
                          <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl border border-blue-100">
                            <p className="text-xs text-slate-500 font-poppins mb-2">Selected Product Path:</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-blue-600 text-white font-poppins">{formData.productType}</Badge>
                              {formData.category && (
                                <>
                                  <ChevronRight className="w-4 h-4 text-slate-400" />
                                  <Badge className="bg-blue-500 text-white font-poppins">{formData.category}</Badge>
                                </>
                              )}
                              {formData.subCategory && (
                                <>
                                  <ChevronRight className="w-4 h-4 text-slate-400" />
                                  <Badge className="bg-blue-400 text-white font-poppins">{formData.subCategory}</Badge>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Product Description */}
                        <div className="mt-6 space-y-2">
                          <Label className="font-poppins text-slate-700">Product Description / Additional Details</Label>
                          <Textarea
                            value={formData.productDescription}
                            onChange={(e) => handleChange("productDescription", e.target.value)}
                            placeholder="Describe your product requirements in detail - specific ingredients, features, target audience, etc."
                            className="min-h-[100px] font-poppins resize-none"
                            data-testid="input-product-description"
                          />
                        </div>

                        <div className="flex justify-between mt-6">
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={() => setActiveSection(1)}
                            className="font-poppins"
                          >
                            Back
                          </Button>
                          <Button 
                            type="button" 
                            onClick={() => setActiveSection(3)}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-poppins"
                          >
                            Next: Requirements <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>

            {/* Section 3: Product Requirements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className={`overflow-hidden transition-all ${activeSection === 3 ? 'ring-2 ring-purple-500 shadow-xl' : 'shadow-lg'}`}>
                <div 
                  className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 cursor-pointer"
                  onClick={() => setActiveSection(3)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Beaker className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white font-poppins">Product Requirements</h2>
                      <p className="text-purple-100 text-sm font-poppins">Specifications and preferences</p>
                    </div>
                  </div>
                </div>
                
                <AnimatePresence>
                  {activeSection === 3 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {/* Texture */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Texture</Label>
                            <Select value={formData.texture} onValueChange={(v) => handleChange("texture", v)}>
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-texture">
                                <SelectValue placeholder="Select texture" />
                              </SelectTrigger>
                              <SelectContent>
                                {TEXTURE_OPTIONS.map(t => (
                                  <SelectItem key={t} value={t} className="font-poppins">{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Conditional "Other" sub-field */}
                            {formData.texture === "Other" && (
                              <Input
                                value={formData.textureOther}
                                onChange={(e) => handleChange("textureOther", e.target.value)}
                                placeholder="Specify texture..."
                                className="h-10 font-poppins mt-2 border-purple-300 focus:border-purple-500"
                                data-testid="input-texture-other"
                              />
                            )}
                          </div>

                          {/* Color */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Color</Label>
                            <Select value={formData.color} onValueChange={(v) => handleChange("color", v)}>
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-color">
                                <SelectValue placeholder="Select color" />
                              </SelectTrigger>
                              <SelectContent>
                                {COLOR_OPTIONS.map(c => (
                                  <SelectItem key={c} value={c} className="font-poppins">{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Conditional "Other" sub-field */}
                            {formData.color === "Other" && (
                              <Input
                                value={formData.colorOther}
                                onChange={(e) => handleChange("colorOther", e.target.value)}
                                placeholder="Specify color..."
                                className="h-10 font-poppins mt-2 border-purple-300 focus:border-purple-500"
                                data-testid="input-color-other"
                              />
                            )}
                          </div>

                          {/* Odour/Fragrance */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Odour / Fragrance</Label>
                            <Select value={formData.odourFragrance} onValueChange={(v) => handleChange("odourFragrance", v)}>
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-odour">
                                <SelectValue placeholder="Select fragrance" />
                              </SelectTrigger>
                              <SelectContent>
                                {ODOUR_FRAGRANCE_OPTIONS.map(o => (
                                  <SelectItem key={o} value={o} className="font-poppins">{o}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Conditional "Other" sub-field */}
                            {formData.odourFragrance === "Other" && (
                              <Input
                                value={formData.odourFragranceOther}
                                onChange={(e) => handleChange("odourFragranceOther", e.target.value)}
                                placeholder="Specify fragrance..."
                                className="h-10 font-poppins mt-2 border-purple-300 focus:border-purple-500"
                                data-testid="input-odour-other"
                              />
                            )}
                          </div>

                          {/* Product Purpose */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Product Purpose</Label>
                            <Select value={formData.productPurpose} onValueChange={(v) => handleChange("productPurpose", v)}>
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-purpose">
                                <SelectValue placeholder="Select purpose" />
                              </SelectTrigger>
                              <SelectContent>
                                {PRODUCT_PURPOSE_OPTIONS.map(p => (
                                  <SelectItem key={p} value={p} className="font-poppins">{p}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Conditional "Other" sub-field */}
                            {formData.productPurpose === "Other" && (
                              <Input
                                value={formData.productPurposeOther}
                                onChange={(e) => handleChange("productPurposeOther", e.target.value)}
                                placeholder="Specify purpose..."
                                className="h-10 font-poppins mt-2 border-purple-300 focus:border-purple-500"
                                data-testid="input-purpose-other"
                              />
                            )}
                          </div>

                          {/* Skin/Hair Type */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Skin/Hair Type</Label>
                            <Select value={formData.skinHairType} onValueChange={(v) => handleChange("skinHairType", v)}>
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-skin-type">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                {SKIN_HAIR_TYPE_OPTIONS.map(t => (
                                  <SelectItem key={t} value={t} className="font-poppins">{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Conditional "Other" sub-field */}
                            {formData.skinHairType === "Other" && (
                              <Input
                                value={formData.skinHairTypeOther}
                                onChange={(e) => handleChange("skinHairTypeOther", e.target.value)}
                                placeholder="Specify skin/hair type..."
                                className="h-10 font-poppins mt-2 border-purple-300 focus:border-purple-500"
                                data-testid="input-skin-type-other"
                              />
                            )}
                          </div>

                          {/* Pack Size */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Pack Size</Label>
                            <Select value={formData.requirementPackSize} onValueChange={(v) => handleChange("requirementPackSize", v)}>
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-pack-size">
                                <SelectValue placeholder="Select size" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {PACK_SIZE_OPTIONS.map(s => (
                                  <SelectItem key={s} value={s} className="font-poppins">{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Conditional "Other" sub-field */}
                            {formData.requirementPackSize === "Other" && (
                              <Input
                                value={formData.requirementPackSizeOther}
                                onChange={(e) => handleChange("requirementPackSizeOther", e.target.value)}
                                placeholder="Specify pack size (e.g., 35KG)..."
                                className="h-10 font-poppins mt-2 border-purple-300 focus:border-purple-500"
                                data-testid="input-pack-size-other"
                              />
                            )}
                          </div>

                          {/* Packaging Preference */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Packaging</Label>
                            <Select value={formData.packagingPreference} onValueChange={(v) => handleChange("packagingPreference", v)}>
                              <SelectTrigger className="h-11 font-poppins" data-testid="select-packaging">
                                <SelectValue placeholder="Select packaging" />
                              </SelectTrigger>
                              <SelectContent>
                                {PACKAGING_OPTIONS.map(p => (
                                  <SelectItem key={p} value={p} className="font-poppins">{p}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Conditional "Other" sub-field */}
                            {formData.packagingPreference === "Other" && (
                              <Input
                                value={formData.packagingPreferenceOther}
                                onChange={(e) => handleChange("packagingPreferenceOther", e.target.value)}
                                placeholder="Specify packaging type..."
                                className="h-10 font-poppins mt-2 border-purple-300 focus:border-purple-500"
                                data-testid="input-packaging-other"
                              />
                            )}
                          </div>

                          {/* Required Quantity */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Required Quantity</Label>
                            <Input
                              value={formData.requiredQuantity}
                              onChange={(e) => handleChange("requiredQuantity", e.target.value)}
                              placeholder="e.g., 1000 units"
                              className="h-11 font-poppins"
                              data-testid="input-quantity"
                            />
                          </div>

                          {/* Expected Delivery */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Expected Delivery</Label>
                            <Input
                              type="date"
                              value={formData.expectedDeliveryDate}
                              onChange={(e) => handleChange("expectedDeliveryDate", e.target.value)}
                              className="h-11 font-poppins"
                              data-testid="input-delivery-date"
                            />
                          </div>
                        </div>

                        {/* Ingredients Preference */}
                        <div className="mt-6 space-y-2">
                          <Label className="font-poppins text-slate-700">Ingredients Preference</Label>
                          <Select value={formData.ingredientsPreference} onValueChange={(v) => handleChange("ingredientsPreference", v)}>
                            <SelectTrigger className="h-11 font-poppins" data-testid="select-ingredients">
                              <SelectValue placeholder="Select ingredient preference" />
                            </SelectTrigger>
                            <SelectContent>
                              {INGREDIENTS_OPTIONS.map(i => (
                                <SelectItem key={i} value={i} className="font-poppins">{i}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/* Conditional "Other" sub-field */}
                          {formData.ingredientsPreference === "Other" && (
                            <Textarea
                              value={formData.ingredientsPreferenceOther}
                              onChange={(e) => handleChange("ingredientsPreferenceOther", e.target.value)}
                              placeholder="List specific ingredients you want included or excluded..."
                              className="min-h-[80px] font-poppins resize-none mt-2 border-purple-300 focus:border-purple-500"
                              data-testid="input-ingredients-other"
                            />
                          )}
                        </div>

                        {/* Reference Image & Remarks */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                          {/* Reference Image */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Reference Image</Label>
                            <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-purple-300 transition-colors">
                              {formData.referenceImageUrl ? (
                                <div className="relative">
                                  <img 
                                    src={formData.referenceImageUrl} 
                                    alt="Reference" 
                                    className="max-h-32 mx-auto rounded-lg object-cover"
                                  />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="sm"
                                    className="absolute top-0 right-0 rounded-full w-6 h-6 p-0"
                                    onClick={() => setFormData(prev => ({ ...prev, referenceImage: null, referenceImageUrl: "" }))}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <label className="cursor-pointer block">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                  />
                                  {uploadingImage ? (
                                    <Loader2 className="w-8 h-8 mx-auto text-purple-500 animate-spin" />
                                  ) : (
                                    <>
                                      <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                                      <p className="text-sm text-slate-500 font-poppins">Click to upload image</p>
                                      <p className="text-xs text-slate-400 font-poppins">Max 5MB</p>
                                    </>
                                  )}
                                </label>
                              )}
                            </div>
                          </div>

                          {/* Remarks */}
                          <div className="space-y-2">
                            <Label className="font-poppins text-slate-700">Additional Remarks</Label>
                            <Textarea
                              value={formData.remarks}
                              onChange={(e) => handleChange("remarks", e.target.value)}
                              placeholder="Any other requirements or notes..."
                              className="min-h-[120px] font-poppins resize-none"
                            />
                          </div>
                        </div>

                        <div className="flex justify-between mt-6">
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={() => setActiveSection(2)}
                            className="font-poppins"
                          >
                            Back
                          </Button>
                          <Button 
                            type="button" 
                            onClick={() => setActiveSection(4)}
                            className="bg-purple-600 hover:bg-purple-700 text-white font-poppins"
                          >
                            Next: Review & Submit <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>

            {/* Section 4: Review & Submit */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className={`overflow-hidden transition-all ${activeSection === 4 ? 'ring-2 ring-emerald-500 shadow-xl' : 'shadow-lg'}`}>
                <div 
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 cursor-pointer"
                  onClick={() => setActiveSection(4)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Send className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-white font-poppins">Review & Submit</h2>
                      <p className="text-emerald-100 text-sm font-poppins">Verify and submit your requirement</p>
                    </div>
                  </div>
                </div>
                
                <AnimatePresence>
                  {activeSection === 4 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardContent className="p-6">
                        {/* Summary Card */}
                        <div className="bg-gradient-to-br from-slate-50 to-emerald-50 rounded-xl p-6 border border-emerald-100">
                          <h3 className="font-semibold text-slate-800 font-poppins mb-4 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-emerald-600" />
                            Requirement Summary
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Client Info Summary */}
                            <div>
                              <h4 className="text-sm font-semibold text-slate-600 font-poppins mb-3 uppercase tracking-wider">Client Information</h4>
                              <div className="space-y-2">
                                <p className="text-sm font-poppins"><span className="text-slate-500">Name:</span> <span className="font-medium text-slate-800">{formData.firstName || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Company:</span> <span className="font-medium text-slate-800">{formData.companyName || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Email:</span> <span className="font-medium text-slate-800">{formData.email || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Phone:</span> <span className="font-medium text-slate-800">{formData.countryCode}{formData.phone || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Location:</span> <span className="font-medium text-slate-800">{formData.location || "-"}</span></p>
                                <div className="text-sm font-poppins flex items-center gap-1"><span className="text-slate-500">Priority:</span> <Badge className={`${
                                  formData.samplePriority === 'High' ? 'bg-red-100 text-red-700' :
                                  formData.samplePriority === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-green-100 text-green-700'
                                }`}>{formData.samplePriority || "-"}</Badge></div>
                              </div>
                            </div>

                            {/* Product Info Summary */}
                            <div>
                              <h4 className="text-sm font-semibold text-slate-600 font-poppins mb-3 uppercase tracking-wider">Product Details</h4>
                              <div className="space-y-2">
                                <p className="text-sm font-poppins"><span className="text-slate-500">Type:</span> <span className="font-medium text-slate-800">{formData.productType || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Category:</span> <span className="font-medium text-slate-800">{formData.category || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Sub-Category:</span> <span className="font-medium text-slate-800">{formData.subCategory || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Texture:</span> <span className="font-medium text-slate-800">{formData.texture || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Color:</span> <span className="font-medium text-slate-800">{formData.color || "-"}</span></p>
                                <p className="text-sm font-poppins"><span className="text-slate-500">Pack Size:</span> <span className="font-medium text-slate-800">{formData.requirementPackSize || "-"}</span></p>
                              </div>
                            </div>
                          </div>

                          {formData.productDescription && (
                            <div className="mt-4 pt-4 border-t border-emerald-100">
                              <h4 className="text-sm font-semibold text-slate-600 font-poppins mb-2">Product Description</h4>
                              <p className="text-sm text-slate-700 font-poppins">{formData.productDescription}</p>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row justify-between gap-4 mt-6">
                          <div className="flex gap-3">
                            <Button 
                              type="button" 
                              variant="outline"
                              onClick={() => setActiveSection(3)}
                              className="font-poppins"
                            >
                              Back
                            </Button>
                            <Button 
                              type="button" 
                              variant="outline"
                              onClick={handleSaveDraft}
                              disabled={savingDraft}
                              className="font-poppins border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              {savingDraft ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4 mr-2" />
                              )}
                              Save as Draft
                            </Button>
                          </div>
                          <Button 
                            type="submit"
                            disabled={submitting}
                            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-poppins px-8 shadow-lg"
                            data-testid="submit-requirement-btn"
                          >
                            {submitting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Submit Requirement
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          </div>
        </form>

        {/* Success Dialog */}
        <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl font-poppins">
                Requirement Submitted Successfully!
              </DialogTitle>
            </DialogHeader>
            
            {submissionResult && (
              <div className="mt-4 space-y-4">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200">
                  <h4 className="font-semibold text-emerald-800 font-poppins mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Submission Details
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                      <span className="text-slate-600 font-poppins">Reference ID:</span>
                      <Badge className="bg-emerald-600 text-white font-mono">{submissionResult.sampleNo}</Badge>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                      <span className="text-slate-600 font-poppins">Client Name:</span>
                      <span className="font-medium text-slate-800 font-poppins">{submissionResult.clientName}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                      <span className="text-slate-600 font-poppins">Company:</span>
                      <span className="font-medium text-slate-800 font-poppins">{submissionResult.companyName}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                      <span className="text-slate-600 font-poppins">Product Type:</span>
                      <span className="font-medium text-slate-800 font-poppins">{submissionResult.productType}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                      <span className="text-slate-600 font-poppins">Category:</span>
                      <span className="font-medium text-slate-800 font-poppins">{submissionResult.category}</span>
                    </div>
                    {submissionResult.subCategory && (
                      <div className="flex justify-between items-center py-2 border-b border-emerald-100">
                        <span className="text-slate-600 font-poppins">Sub-Category:</span>
                        <span className="font-medium text-slate-800 font-poppins">{submissionResult.subCategory}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2">
                      <span className="text-slate-600 font-poppins">Priority:</span>
                      <Badge className={`${
                        submissionResult.priority === 'High' ? 'bg-red-100 text-red-700' :
                        submissionResult.priority === 'Medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>{submissionResult.priority}</Badge>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-emerald-200">
                    <div className="flex items-center gap-2">
                      {submissionResult.syncedToZoho ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-emerald-700 font-poppins">Synced to Zoho Creator</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-amber-700 font-poppins">Pending Zoho sync</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-center">
                  <Button 
                    variant="outline" 
                    onClick={handleReset}
                    className="font-poppins"
                  >
                    Submit Another
                  </Button>
                  {user ? (
                    <Button 
                      onClick={() => navigate('/')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-poppins"
                    >
                      Go to Dashboard
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => navigate('/login')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-poppins"
                    >
                      Login to Dashboard
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Footer */}
        <motion.div 
          className="text-center mt-8 pb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-sm text-slate-400 font-poppins">
            © {new Date().getFullYear()} V Cosmetic Crafters Private Limited. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
